<template>
    <div id="ql-app" class="app-print-mode">
        <md-button class="btn-print md-primary md-raised" @click="print" @touchstart="print">{{ printButtonLabel }}</md-button>
        <div class="print-pages-viewer" v-if="loaded" :class="layout">
            <project-print-pages v-for="page in projectPages"
                @pageReadyFun="handlePageReady"
                :page="page"
                :showMode="showMode"
                :locked="true"
                :landscape="layout=='landscape'"
                :key="page.pageNumber"
                :versionId="versionId"
            />
        </div>
        <md-layout class="loader-wrapper" v-if="!loadPageReady">
            <div class="loader">
                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24"><g fill="none" stroke="currentColor" stroke-linecap="round" stroke-width="2"><path stroke-dasharray="2 4" stroke-dashoffset="6" d="M12 3C16.9706 3 21 7.02944 21 12C21 16.9706 16.9706 21 12 21"><animate attributeName="stroke-dashoffset" dur="0.6s" repeatCount="indefinite" values="6;0"/></path><path stroke-dasharray="30" stroke-dashoffset="30" d="M12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3"><animate fill="freeze" attributeName="stroke-dashoffset" begin="0.1s" dur="0.3s" values="30;0"/></path><path stroke-dasharray="10" stroke-dashoffset="10" d="M12 8v7.5"><animate fill="freeze" attributeName="stroke-dashoffset" begin="0.5s" dur="0.2s" values="10;0"/></path><path stroke-dasharray="6" stroke-dashoffset="6" d="M12 15.5l3.5 -3.5M12 15.5l-3.5 -3.5"><animate fill="freeze" attributeName="stroke-dashoffset" begin="0.7s" dur="0.2s" values="6;0"/></path></g></svg>
            </div>
        </md-layout>
    </div>
</template>

<script>
// Vendor Libraries
import PDFJS from 'pdfjs-dist/webpack';
import firebase from 'firebase/app';
import { db } from '@/main';
import { mapGetters } from 'vuex';
// Buses
import NavBus from '@/buses/nav';
// App Components
import ProjectPrintPages from '@/components/print/print-pages';
import Utils from '@/lib/Utils';
import { beginPrintLoad, isCurrentPrintLoad } from '@/lib/print-load';

PDFJS.workerSrc = '../../../node_modules/pdfjs-dist/build/pdf.worker.js';

const PrintView = {
    name: 'print',
    props: ['id', 'versionId', 'layout'],
    data: function() {
        return {
            projectRef: db.collection('projects').doc(this.id),
            currentPage: 1,
            pdfPages: [],
            projectPages: [],
            theme: 'default',
            loaded: false,
            printMode: false,
            layoutStyle: null,
            loadPageReady: false,
            versionReady: false,
            paperMode: localStorage.getItem('print-paper-mode') || 'screen'
        };
    },
    computed: {
        ...mapGetters([
            'project',
            'documentUrl',
            'user',
            'showMode'
        ]),
        totalPages() {
            return this.pdfPages.length;
        },
        isIpad() {
            return /iPad|Macintosh/i.test(navigator.userAgent) && 'ontouchend' in document;
        },
        paperModeLabel() {
            const labels = {
                screen: 'Match app view (no scaling)',
                letter: 'Fit to US Letter',
                a4: 'Fit to A4'
            };
            return labels[this.paperMode] || labels.screen;
        },
        isSafari() {
            return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        },
        printButtonLabel() {
            return this.isSafari ? 'Save as PDF' : 'Print';
        },
        version: function() {
            this.$store.dispatch('useVersion', {
                versionID: this.versionId || null,
                callback: () => {
                }
            });
        }
    },
    watch: {
        documentUrl: function() {
            // Only the version's PDF: before useVersion lands, documentUrl is still the live show's.
            if (this.project && !this.loaded && (this.versionReady || !this.versionId)) {
                this.initDocument();
            }
        }
    },
    methods: {
        setPrintMode(val) {
            this.printMode = val;
        },
        handlePageReady() {
        },
        initDocument: function() {
            if (this.documentUrl) {
                const token = beginPrintLoad(this);
                this.loadDocument(this.documentUrl).then((pdfDoc) => {
                    if (!isCurrentPrintLoad(this, token)) return;
                    this.pdfDoc = pdfDoc;
                    this.loadInitialPages(token);
                });
            } else {
                console.error('Could not init document');
            }
        },
        loadInitialPages: function(token) {
            const initialBatchSize = this.isIpad ? 1 : 20;
            this.loadPagesBatch(1, initialBatchSize, token);
        },
        loadPagesBatch: function(startPage, batchSize, token) {
            const endPage = Math.min(startPage + (batchSize - 1), this.pdfDoc.numPages);
            const pdfPageProms = [];
            for (let i = startPage; i <= endPage; i++) {
                const prom = this.pdfDoc.getPage(i);
                pdfPageProms.push(prom);
            }
            Promise.all(pdfPageProms).then((pdfPages) => {
                if (!isCurrentPrintLoad(this, token)) return;
                for (let index = 0; index < pdfPages.length; index++) {
                    const page = pdfPages[index];
                    const newPage = {
                        pageNumber: startPage + index,
                        pdfPage: page
                    };
                    this.projectPages.push(newPage);
                }

                if (endPage >= this.pdfDoc.numPages) {
                    this.loadPageReady = true;
                } else {
                    const batchTimeOut = this.isIpad ? 5000 : 500;
                    setTimeout(() => this.loadPagesBatch(endPage + 1, batchSize, token), batchTimeOut);
                }
            });
            this.loaded = true;
        },
        loadDocument: function(documentSrc) {
            this.pdfDoc = null;

            const loadingTask = PDFJS.getDocument(documentSrc);

            loadingTask.onProgress = (status) => {
                const ratio = status.loaded / status.total;
                this.loadingProgress = Math.min(ratio, 1);
            };

            return loadingTask.promise;
        },
        reload: function() {
            this.loaded = false;
            this.initDocument();
        },
        print: function() {
            // Don't print before all pages have streamed in and laid out, or we
            // would measure a partial DOM and size the @page wrong.
            if (!this.loadPageReady) {
                return;
            }
            const mode = localStorage.getItem('print-paper-mode') || 'screen';

            // Freeze per-page offset recomputation for the whole print sequence
            // (covers the pre-print zoom relayout, before `beforeprint` fires).
            window.qlIsPrinting = true;

            // Measure after a frame so layout is settled before we read rects.
            requestAnimationFrame(() => {
                const els = Array.prototype.slice.call(
                    document.querySelectorAll('.ql-pages-inner')
                );
                const { css, zoom } = Utils.buildPrintPageStyle(mode, els);

                const style = document.createElement('style');
                style.setAttribute('data-print-page-size', 'true');
                style.innerHTML = css;
                document.head.appendChild(style);

                // Fit modes: `zoom` reflows the layout box (unlike `transform`),
                // so pagination stays correct and the overlay scales with the PDF.
                if (zoom !== 1) {
                    els.forEach((el) => { el.style.zoom = zoom; });
                }

                // Tear down on afterprint, NOT a timer (see print.vue for rationale).
                let cleaned = false;
                const cleanup = () => {
                    if (cleaned) return;
                    cleaned = true;
                    if (style.parentNode) style.parentNode.removeChild(style);
                    if (zoom !== 1) els.forEach((el) => { el.style.zoom = ''; });
                    window.qlIsPrinting = false;
                    window.removeEventListener('afterprint', cleanup);
                };
                window.addEventListener('afterprint', cleanup);

                const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
                if (isSafari) {
                    document.execCommand('print', false, null);
                } else {
                    window.print();
                }
                setTimeout(cleanup, 60000);
            });
        }
    },
    components: {
        'project-print-pages': ProjectPrintPages
    },
    beforeRouteEnter: function(to, from, next) {
        next((vm) => {
            // The whole-users-collection binding (setUsersRef) was removed 23 Sept 2026:
            // its only reader was the institution picker, now a server-side search
            // (searchInstitutions). It streamed every user record into this page.
            vm.$store.dispatch('setActiveUser', {
                ref: db.collection('users').doc(firebase.auth().currentUser.uid),
                callback: () => {}
            });
            vm.$store.dispatch('setActiveProject', {
                ref: db.collection('projects').doc(vm.id),
                callback: () => {
                    // versionId is the route prop. This tested the `version` computed, which only dispatches
                    // useVersion and returns nothing, so the page always took the live path below (23 Oct 2026).
                    if (vm.versionId) {
                        vm.$store.dispatch('useVersion', {
                            versionID: vm.versionId,
                            callback: () => {
                                vm.versionReady = true;
                                vm.reload(1, 'version');
                            }
                        });
                    } else {
                        vm.reload(1, 'setActiveProject');
                    }
                }
            });
            vm.$store.dispatch('setInvitationsRef', {
                ref: db.collection('invitations'),
                callback: () => {}
            });
        });
    },
    created: function() {
        NavBus.$on('reloadCurrentProject', ({newPagePosition}) => {
            this.reload(newPagePosition);
        });
    },
    beforeDestroy: function() {
        NavBus.$off('reloadCurrentProject');
    },
    mounted: function() {
    }
};

export default PrintView;
</script>

<style lang="scss">
    * {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
    }
    .print-pages-viewer {
        display: block;
    }
    .btn-print {
        position: fixed;
        z-index: 9999;
    }
    .print-notice {
        position: fixed;
        z-index: 9999;
        top: 6px;
        left: 240px;
        width: 400px;
        padding: 10px;
        background: rgba(255, 255, 255, 0.9);
        border: solid 4px #5ecfdf;
    }
    .app-print-mode {
        .loader-wrapper {
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background-color: rgba(0, 0, 0, 0.575);
            z-index: 99999;
            display: flex;
            justify-content: center;
            align-items: center;

            .loader, svg {
                width: 64px;
                height: 64px;
            }

            svg {
                color: #fff;
            }
        }
        .md-card {
            overflow: unset
        }
    }
    @media all {
        .btn-print, .print-notice {
            visibility: visible;
        }
    }
    @media print {
        .btn-print, .print-notice {
            visibility: hidden;
        }
    }
</style>


// WEBPACK FOOTER //
// printVersion.vue?560f51f4